import {
  DashboardEventTypeDto,
  DashboardFunnelStageDto,
  DashboardRegionTypeDto,
  GenderDto,
  QuizScoreDto,
} from '@/api/generated';
import { type PolishRegion } from '@/views/admin/dashboard/map/regions.ts';

export type DashboardTranslations = {
  areas: {
    firstChoice: string;
    otherChoice: string;
    subtitle: string;
    title: string;
  };
  close: string;
  dataView: string;
  error: string;
  feed: {
    empty: string;
    events: Record<DashboardEventTypeDto, string>;
    subtitle: string;
    title: string;
  };
  filter: {
    clear: string;
    region: string;
    types: Record<DashboardRegionTypeDto, string>;
  };
  funnel: {
    ofRegistered: string;
    stages: Record<DashboardFunnelStageDto, string>;
    stepConversion: string;
    subtitle: string;
    title: string;
  };
  heatmap: {
    sessions: string;
    subtitle: string;
    title: string;
  };
  kpis: {
    averageQuizMinutes: string;
    averageQuizPoints: string;
    candidates: string;
    conversion: string;
    conversionHint: string;
    fairCandidates: string;
    minutes: string;
    noPrevious: string;
    quizzesFinished: string;
    recommended: string;
    vsPrevious: string;
  };
  live: string;
  map: {
    candidates: string;
    clickHint: string;
    layers: Record<DashboardRegionTypeDto, string>;
    outside: Record<DashboardRegionTypeDto, string>;
    ranking: string;
    share: string;
    subtitle: Record<DashboardRegionTypeDto, string>;
    title: string;
  };
  periods: {
    all: string;
    days30: string;
    days7: string;
    days90: string;
    label: string;
  };
  presentation: {
    start: string;
    stop: string;
  };
  profile: {
    gender: string;
    genders: Record<GenderDto, string>;
    languages: string;
    studentStatus: string;
    subtitle: string;
    title: string;
    typeOfStudy: string;
    universities: string;
    yearOfStudy: string;
  };
  quiz: {
    candidates: string;
    notRecommended: string;
    points: string;
    recommended: string;
    scores: Record<QuizScoreDto, string>;
    subtitle: string;
    title: string;
  };
  regions: Record<PolishRegion, string>;
  saveImage: string;
  skills: {
    assessments: string;
    languages: string;
    strongShare: string;
    subtitle: string;
    title: string;
  };
  subtitle: string;
  timeline: {
    fairRegistrations: string;
    quizzesFinished: string;
    registrations: string;
    subtitle: string;
    title: string;
  };
  title: string;
  updatedAt: string;
};

export const dashboardTranslations: DashboardTranslations = {
  areas: {
    firstChoice: 'pierwszy wybór',
    otherChoice: 'drugi lub trzeci wybór',
    subtitle: 'Kandydaci, którzy wybrali obszar',
    title: 'Obszary zainteresowań',
  },
  close: 'Zamknij',
  dataView: 'Dane w tabeli',
  error: 'Nie udało się pobrać statystyk. Spróbuj ponownie za chwilę.',
  feed: {
    empty: 'W tym okresie nic się jeszcze nie wydarzyło',
    events: {
      QUIZ_FINISHED: 'Ukończony quiz',
      QUIZ_STARTED: 'Rozpoczęty quiz',
      REGISTERED: 'Nowy kandydat',
    },
    subtitle: 'Najnowsze zdarzenia, bez danych osobowych',
    title: 'Na bieżąco',
  },
  filter: {
    clear: 'Wyczyść filtr',
    region: '{{region}} · {{type}}',
    types: {
      INTERNSHIP: 'miejsce stażu',
      STUDY: 'uczelnia',
    },
  },
  funnel: {
    ofRegistered: '{{value}} zarejestrowanych',
    stages: {
      EMAIL_VERIFIED: 'Potwierdzony e-mail',
      PERSONAL_DATA: 'Dane osobowe',
      QUIZ_FINISHED: 'Ukończony quiz',
      QUIZ_STARTED: 'Rozpoczęty quiz',
      RECOMMENDED: 'Rekomendacja',
      REGISTERED: 'Rejestracja',
    },
    stepConversion: '{{value}} z poprzedniego etapu',
    subtitle: 'Ilu kandydatów dociera do kolejnych etapów',
    title: 'Lejek rekrutacji',
  },
  heatmap: {
    sessions: 'sesje quizu',
    subtitle: 'Kiedy kandydaci rozwiązują quiz (czas polski)',
    title: 'Aktywność w tygodniu',
  },
  kpis: {
    averageQuizMinutes: 'Średni czas quizu',
    averageQuizPoints: 'Średni wynik quizu',
    candidates: 'Kandydaci',
    conversion: 'Skuteczność',
    conversionHint: 'rekomendowani wśród zarejestrowanych',
    fairCandidates: 'Z targów pracy',
    minutes: '{{value}} min',
    noPrevious: 'od początku rekrutacji',
    quizzesFinished: 'Ukończone quizy',
    recommended: 'Rekomendowani',
    vsPrevious: 'vs poprzednie {{days}} dni',
  },
  live: 'Na żywo',
  map: {
    candidates: 'kandydaci',
    clickHint: 'Kliknij województwo, aby zawęzić cały dashboard',
    layers: {
      INTERNSHIP: 'Miejsce stażu',
      STUDY: 'Uczelnia',
    },
    outside: {
      INTERNSHIP: '{{count}} chce odbyć staż w dowolnym miejscu w Polsce',
      STUDY: '{{count}} studiuje na uczelni spoza listy',
    },
    ranking: 'Najwięcej kandydatów',
    share: 'udział',
    subtitle: {
      INTERNSHIP: 'Województwa i miasta, w których kandydaci chcą odbyć staż',
      STUDY: 'Województwa, w których studiują kandydaci',
    },
    title: 'Skąd są kandydaci',
  },
  periods: {
    all: 'Cała rekrutacja',
    days30: '30 dni',
    days7: '7 dni',
    days90: '90 dni',
    label: 'Okres',
  },
  presentation: {
    start: 'Tryb prezentacji',
    stop: 'Zakończ prezentację',
  },
  profile: {
    gender: 'Płeć',
    genders: {
      FEMALE: 'kobiety',
      MALE: 'mężczyźni',
      UNKNOWN: 'nieustalona',
    },
    languages: 'Język aplikacji',
    studentStatus: 'Status',
    subtitle: 'Kim są kandydaci, którzy podali swoje dane',
    title: 'Profil kandydatów',
    typeOfStudy: 'Rodzaj studiów',
    universities: 'Najczęściej wybierane uczelnie',
    yearOfStudy: 'Rok studiów',
  },
  quiz: {
    candidates: 'kandydaci',
    notRecommended: 'bez rekomendacji',
    points: 'pkt',
    recommended: 'z rekomendacją',
    scores: {
      AVERAGE: 'Przeciętny',
      GOOD: 'Dobry',
      LOW: 'Niski',
      OUTSTANDING: 'Wybitny',
      VERY_GOOD: 'Bardzo dobry',
    },
    subtitle: 'Rozkład punktów w ukończonych quizach i progi ocen',
    title: 'Wyniki quizu',
  },
  regions: {
    'PL-02': 'dolnośląskie',
    'PL-04': 'kujawsko-pomorskie',
    'PL-06': 'lubelskie',
    'PL-08': 'lubuskie',
    'PL-10': 'łódzkie',
    'PL-12': 'małopolskie',
    'PL-14': 'mazowieckie',
    'PL-16': 'opolskie',
    'PL-18': 'podkarpackie',
    'PL-20': 'podlaskie',
    'PL-22': 'pomorskie',
    'PL-24': 'śląskie',
    'PL-26': 'świętokrzyskie',
    'PL-28': 'warmińsko-mazurskie',
    'PL-30': 'wielkopolskie',
    'PL-32': 'zachodniopomorskie',
  },
  saveImage: 'Zapisz jako obraz',
  skills: {
    assessments: 'ocen',
    languages: 'Języki: ocena dobra lub zaawansowana',
    strongShare: 'ocenia się na poziomie dobrym lub zaawansowanym',
    subtitle: 'Odsetek kandydatów, którzy oceniają się dobrze lub bardzo dobrze',
    title: 'Najmocniejsze kompetencje',
  },
  subtitle: '{{from}} – {{to}}',
  timeline: {
    fairRegistrations: 'rejestracje na targach',
    quizzesFinished: 'ukończone quizy',
    registrations: 'rejestracje',
    subtitle: 'Kandydaci dzień po dniu',
    title: 'Napływ kandydatów',
  },
  title: 'Rekrutacja w liczbach',
  updatedAt: 'Aktualizacja {{time}}',
};
